/**
 * Admin Settings Guide
 * System configuration documentation
 */

import {
  Settings,
  Wrench,
  Clock,
  Bell,
  Calendar,
  Users,
  Building,
} from 'lucide-react';
import { pageStyles, cardStyles } from '../../lib/styles';

export function AdminSettings() {
  return (
    <div className={pageStyles.container}>
      {/* Introduction */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Settings className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Configuracion del Sistema</span>
          </div>
        </div>
        <div className={`${cardStyles.content} prose prose-gray max-w-none`}>
          <p>
            Como administrador, puedes configurar todos los aspectos del
            sistema. Esta guia explica las diferentes opciones de configuracion
            disponibles.
          </p>
          <div className="not-prose mt-4 p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
            <strong>Acceso:</strong> Estas opciones solo estan disponibles para
            usuarios con rol de Administrador.
          </div>
        </div>
      </div>

      {/* Service Types */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Wrench className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Tipos de Servicio</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <p className="text-gray-600 mb-4">
            Define los servicios que ofrece tu taller y que los clientes pueden
            reservar online.
          </p>
          <div className="space-y-3">
            <div className="p-3 border rounded-lg">
              <h4 className="font-medium text-gray-900">Nombre</h4>
              <p className="text-sm text-gray-600">
                Nombre del servicio visible para los clientes (ej: "Revision
                General", "Cambio de Aceite")
              </p>
            </div>
            <div className="p-3 border rounded-lg">
              <h4 className="font-medium text-gray-900">Descripcion</h4>
              <p className="text-sm text-gray-600">
                Texto descriptivo que ayuda al cliente a entender que incluye
                el servicio
              </p>
            </div>
            <div className="p-3 border rounded-lg">
              <h4 className="font-medium text-gray-900">Duracion Estimada</h4>
              <p className="text-sm text-gray-600">
                Tiempo aproximado en minutos que dura el servicio
              </p>
            </div>
            <div className="p-3 border rounded-lg">
              <h4 className="font-medium text-gray-900">Slots por Hora</h4>
              <p className="text-sm text-gray-600">
                Cuantas citas de este tipo puedes atender por hora
              </p>
            </div>
            <div className="p-3 border rounded-lg">
              <h4 className="font-medium text-gray-900">Activo</h4>
              <p className="text-sm text-gray-600">
                Si esta desactivado, el servicio no aparece en el widget de
                reservas
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bays */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Building className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Bahias de Trabajo</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <p className="text-gray-600 mb-4">
            Las bahias representan las areas de trabajo fisicas de tu taller
            (boxes, elevadores, zonas de trabajo).
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-1">Mecanica</h4>
              <p className="text-sm text-blue-700">
                Para trabajos mecanicos generales
              </p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg">
              <h4 className="font-medium text-orange-900 mb-1">Carroceria</h4>
              <p className="text-sm text-orange-700">
                Para trabajos de chapa y pintura
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-medium text-purple-900 mb-1">Diagnostico</h4>
              <p className="text-sm text-purple-700">
                Para diagnosticos electronicos
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-2 text-sm text-gray-600">
            <p>
              <strong>Trabajos Simultaneos:</strong> Define cuantos vehiculos
              pueden trabajarse a la vez en cada bahia (1-10).
            </p>
            <p>
              <strong>Estado Activo:</strong> Las bahias inactivas no se usan
              para asignar nuevos trabajos.
            </p>
          </div>
        </div>
      </div>

      {/* Booking Rules */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Reglas de Reserva</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="p-3 border rounded-lg">
                <h4 className="font-medium text-gray-900">
                  Antelacion Minima
                </h4>
                <p className="text-sm text-gray-600">
                  Horas de antelacion minima para reservar (ej: 24h significa
                  que no pueden reservar para hoy)
                </p>
              </div>
              <div className="p-3 border rounded-lg">
                <h4 className="font-medium text-gray-900">
                  Antelacion Maxima
                </h4>
                <p className="text-sm text-gray-600">
                  Dias maximo de antelacion (ej: 30 dias permite reservar hasta
                  un mes adelante)
                </p>
              </div>
              <div className="p-3 border rounded-lg">
                <h4 className="font-medium text-gray-900">
                  Duracion de Slot
                </h4>
                <p className="text-sm text-gray-600">
                  Duracion en minutos de cada franja horaria (ej: 60 min = citas
                  cada hora)
                </p>
              </div>
              <div className="p-3 border rounded-lg">
                <h4 className="font-medium text-gray-900">
                  Buffer entre Citas
                </h4>
                <p className="text-sm text-gray-600">
                  Minutos de margen entre citas para limpieza/preparacion
                </p>
              </div>
            </div>

            <div className="p-3 border rounded-lg">
              <h4 className="font-medium text-gray-900">
                Permitir Mismo Dia
              </h4>
              <p className="text-sm text-gray-600">
                Si se permite, los clientes pueden reservar para el mismo dia
                (respetando la antelacion minima en horas)
              </p>
            </div>

            <div className="p-3 border rounded-lg">
              <h4 className="font-medium text-gray-900">
                Ventana de Cancelacion
              </h4>
              <p className="text-sm text-gray-600">
                Horas antes de la cita hasta cuando el cliente puede cancelar
                sin penalizacion
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Bell className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Notificaciones</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <p className="text-gray-600 mb-4">
            Configura que notificaciones automaticas se envian a los clientes.
          </p>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900">
                  Confirmacion de Cita
                </h4>
                <p className="text-sm text-gray-500">
                  Email al crear una nueva cita
                </p>
              </div>
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                Recomendado
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900">Recordatorio 24h</h4>
                <p className="text-sm text-gray-500">
                  Email un dia antes de la cita
                </p>
              </div>
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                Recomendado
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900">Recordatorio 2h</h4>
                <p className="text-sm text-gray-500">
                  Email 2 horas antes de la cita
                </p>
              </div>
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                Opcional
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900">
                  Cambios de Estado
                </h4>
                <p className="text-sm text-gray-500">
                  Notificar cuando cambia el estado de la reparacion
                </p>
              </div>
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                Recomendado
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900">
                  Vehiculo Listo
                </h4>
                <p className="text-sm text-gray-500">
                  Notificacion cuando el vehiculo esta listo para recoger
                </p>
              </div>
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                Recomendado
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Staff Management */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Gestion de Personal</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <p className="text-gray-600 mb-4">
            Administra los usuarios que tienen acceso al sistema.
          </p>
          <div className="space-y-3">
            <div className="p-3 border rounded-lg">
              <h4 className="font-medium text-gray-900">Crear Usuario</h4>
              <p className="text-sm text-gray-600">
                Anade nuevos miembros del equipo con email y rol asignado
              </p>
            </div>
            <div className="p-3 border rounded-lg">
              <h4 className="font-medium text-gray-900">Asignar Roles</h4>
              <p className="text-sm text-gray-600">
                Define si el usuario es Administrador, Manager o Staff
              </p>
            </div>
            <div className="p-3 border rounded-lg">
              <h4 className="font-medium text-gray-900">
                Desactivar Usuarios
              </h4>
              <p className="text-sm text-gray-600">
                Los usuarios desactivados no pueden acceder al sistema
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Business Hours */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Horario del Taller</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <p className="text-gray-600 mb-4">
            Define el horario de apertura para cada dia de la semana. Los
            clientes solo podran reservar dentro de estos horarios.
          </p>
          <div className="grid gap-2 text-sm">
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span>Lunes - Viernes</span>
              <span className="font-mono">08:00 - 18:00</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span>Sabado</span>
              <span className="font-mono">09:00 - 14:00</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded text-gray-400">
              <span>Domingo</span>
              <span>Cerrado</span>
            </div>
          </div>
            <p className="text-sm text-gray-500 mt-3">
              Puedes definir horarios diferentes para cada dia, incluyendo
              pausas para comida.
            </p>
        </div>
      </div>
    </div>
  );
}

export default AdminSettings;
